/**
 * MapCustomerModal — pick a Sage customer for a Faire retailer that the
 * fuzzy matcher couldn't auto-resolve. Used for both the "Map customer"
 * (no_candidate) and "Pick one" (ambiguous) variants from the Issues page.
 *
 * Behavior:
 *   - Shows the retailer's name + Faire retailer_id at the top.
 *   - If the error came with fuzzy candidates (ambiguous), they're rendered
 *     as a one-click pick list at the top of the search results.
 *   - Free-form search box hits sage_ar_customer_mirror via /customers/search.
 *   - Confirm writes the mapping + flips the state row + resolves the error
 *     in a single POST.
 *
 * Out of scope for this version: creating a NEW Sage customer in-app. The
 * Sage-side VI customer-create job is Phase 1.5 in the spec. If the
 * operator can't find the right customer, they create it in Sage manually,
 * wait for the next mirror sync, then come back and map it here.
 */

import { useState, useEffect, useMemo } from 'react';
import { X, Search, Check, Loader2, ExternalLink } from 'lucide-react';

import {
  useFaireCustomerSearch,
  useFaireCustomerMap,
  useFaireRetailerLookup,
  type SageCustomerRow,
} from '@/lib/useFaireCustomers';
import type { FaireError } from '@/lib/useFaireRuns';

type Props = {
  open: boolean;
  onClose: () => void;
  errorRow: FaireError | null;
};

function formatAddress(c: SageCustomerRow): string {
  const parts = [c.City, c.State, c.ZipCode].filter(Boolean);
  return parts.join(', ');
}

export function MapCustomerModal({ open, onClose, errorRow }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SageCustomerRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch the live retailer details from Faire so we can show the address
  // and pre-scope the search by state. Skipped if no retailer_id on the row.
  const retailerLookup = useFaireRetailerLookup(
    open ? errorRow?.context.retailer_id ?? null : null,
    open ? errorRow?.faire_order_id ?? null : null,
  );
  const retailerState = retailerLookup.data?.address.state_region ?? undefined;

  // Reset search when the modal opens. We seed with the retailer name from
  // the error context; once the Faire lookup resolves we prefer the live
  // address city as a stronger search hint (matchers often miss on stale
  // addresses, so the live one is what we want to mirror in Sage).
  useEffect(() => {
    if (open && errorRow) {
      setSearch(errorRow.context.retailer_name ?? '');
      setSelected(null);
      setError(null);
    }
  }, [open, errorRow]);

  const searchQ = useFaireCustomerSearch(search, {
    enabled: open,
    state: retailerState && /^[A-Z]{2}$/.test(retailerState) ? retailerState : undefined,
  });
  const mapMutation = useFaireCustomerMap();

  const candidateNos = useMemo(
    () => new Set((errorRow?.context.candidates ?? []).map((c) => c.sage_customer_no)),
    [errorRow],
  );
  const candidateRows = useMemo<SageCustomerRow[]>(
    () => searchQ.customers.filter((c) => candidateNos.has(c.CustomerNo)),
    [searchQ.customers, candidateNos],
  );
  const otherRows = useMemo<SageCustomerRow[]>(
    () => searchQ.customers.filter((c) => !candidateNos.has(c.CustomerNo)),
    [searchQ.customers, candidateNos],
  );

  if (!open || !errorRow) return null;

  const handleConfirm = () => {
    if (!selected) return;
    if (!errorRow.context.retailer_id) {
      setError("Can't map: the error row has no retailer_id. Open the order in Faire to grab it.");
      return;
    }
    setError(null);
    mapMutation.mutate(
      {
        faire_retailer_id: errorRow.context.retailer_id,
        sage_customer_no: selected.CustomerNo,
        business_name: errorRow.context.retailer_name ?? selected.CustomerName,
        mapped_city: selected.City ?? undefined,
        mapped_state: selected.State ?? undefined,
        resolve_error_id: errorRow.error_id,
        update_state_for_faire_order_id: errorRow.faire_order_id ?? undefined,
      },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (e) => {
          setError(e.message);
        },
      },
    );
  };

  const RowItem = ({ c, badge }: { c: SageCustomerRow; badge?: string }) => {
    const isSelected = selected?.CustomerNo === c.CustomerNo;
    return (
      <li>
        <button
          type="button"
          onClick={() => setSelected(c)}
          className={`w-full text-left border rounded-md px-3 py-2 transition-colors ${
            isSelected
              ? 'border-amp-olive bg-amp-cream-warm dark:border-amp-olive-dark dark:bg-amp-dark-surface-warm'
              : 'border-amp-line hover:border-amp-olive dark:border-amp-dark-line dark:hover:border-amp-olive-dark'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-amp-ink dark:text-amp-dark-ink truncate">
                {c.CustomerName}
              </div>
              <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {c.CustomerNo} {formatAddress(c) ? ` - ${formatAddress(c)}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {badge ? (
                <span className="text-[10px] uppercase tracking-wide bg-amp-olive text-amp-white dark:bg-amp-olive-dark px-1.5 py-0.5 rounded">
                  {badge}
                </span>
              ) : null}
              {isSelected ? <Check className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" /> : null}
            </div>
          </div>
        </button>
      </li>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => (!mapMutation.isPending ? onClose() : null)}
      />
      <div className="relative bg-amp-white dark:bg-amp-dark-surface border border-amp-line dark:border-amp-dark-line rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-amp-line dark:border-amp-dark-line">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">
              {candidateRows.length > 0 ? 'Pick the right Sage customer' : 'Map a new retailer'}
            </h2>
            <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-0.5">
              <strong className="text-amp-ink dark:text-amp-dark-ink">
                {retailerLookup.data?.name ?? errorRow.context.retailer_name ?? '(unknown retailer)'}
              </strong>
              {errorRow.context.retailer_id ? (
                <> | Faire id <code className="font-mono">{errorRow.context.retailer_id}</code></>
              ) : null}
              {errorRow.faire_order_id ? (
                <>
                  {' | '}
                  <a
                    href={`https://www.faire.com/brand-portal/orders/${errorRow.faire_order_id.toLowerCase()}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-amp-olive dark:text-amp-olive-dark hover:underline"
                  >
                    open order in Faire <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              ) : null}
            </p>
            {/* Live retailer address from the Faire API */}
            {retailerLookup.isLoading ? (
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1 italic">
                Looking up address from Faire...
              </p>
            ) : retailerLookup.data ? (
              (() => {
                const a = retailerLookup.data.address;
                const lineParts = [a.line_1, a.line_2].filter(Boolean);
                const cityParts = [a.city, a.state_region, a.postal_code].filter(Boolean);
                if (lineParts.length === 0 && cityParts.length === 0) return null;
                return (
                  <p className="text-xs text-amp-ink dark:text-amp-dark-ink mt-1">
                    {lineParts.join(', ')}
                    {lineParts.length > 0 && cityParts.length > 0 ? ' • ' : ''}
                    {cityParts.join(', ')}
                  </p>
                );
              })()
            ) : retailerLookup.isError ? (
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1 italic">
                Could not load address from Faire ({retailerLookup.error.message.slice(0, 60)}).
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mapMutation.isPending}
            className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-amp-line dark:border-amp-dark-line">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amp-ink-muted dark:text-amp-dark-ink-muted" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Sage customers by name, city, or zip..."
              className="w-full border border-amp-line rounded-md pl-9 pr-3 py-2 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
            />
          </div>
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1.5">
            Records marked DNU are filtered out automatically.
          </p>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {searchQ.isLoading ? (
            <div className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching...
            </div>
          ) : search.trim().length < 2 ? (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Type at least 2 characters to search.
            </p>
          ) : searchQ.customers.length === 0 ? (
            <div className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
              <p className="mb-2">No Sage customers match this search.</p>
              <p>
                If this retailer doesn't exist in Sage yet, create them in Sage first
                (the customer-create automation is on the Phase 1.5 roadmap),
                wait for the next mirror sync, then come back and map.
              </p>
            </div>
          ) : (
            <>
              {candidateRows.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-2">
                    Suggested by the matcher
                  </h3>
                  <ul className="space-y-2">
                    {candidateRows.map((c) => {
                      const score = errorRow.context.candidates.find(
                        (cand) => cand.sage_customer_no === c.CustomerNo,
                      )?.score;
                      return (
                        <RowItem
                          key={c.CustomerNo}
                          c={c}
                          badge={score != null ? `${Math.round(score * 100)}%` : undefined}
                        />
                      );
                    })}
                  </ul>
                </div>
              )}
              {otherRows.length > 0 && (
                <div>
                  {candidateRows.length > 0 && (
                    <h3 className="text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2">
                      Other search results
                    </h3>
                  )}
                  <ul className="space-y-2">
                    {otherRows.map((c) => (
                      <RowItem key={c.CustomerNo} c={c} />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-amp-line dark:border-amp-dark-line">
          {error ? (
            <div className="mb-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
              {error}
            </div>
          ) : null}
          {selected ? (
            <div className="mb-3 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Mapping <strong className="text-amp-ink dark:text-amp-dark-ink">{errorRow.context.retailer_name ?? errorRow.context.retailer_id}</strong>
              {' '}to{' '}
              <strong className="text-amp-ink dark:text-amp-dark-ink">{selected.CustomerName}</strong>
              {' '}<code className="font-mono">({selected.CustomerNo})</code>.
              Future orders from this retailer will auto-match.
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={mapMutation.isPending}
              className="text-sm border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-50 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selected || mapMutation.isPending}
              className="text-sm bg-amp-olive text-amp-white rounded px-3 py-1.5 hover:bg-amp-olive-dark disabled:cursor-not-allowed disabled:opacity-50 inline-flex items-center gap-1"
            >
              {mapMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Confirm mapping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
